// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseAvatar,
  BaseFilters,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
// Others
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IPayloadCallApiFn,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DEFAULT_SORT_QUOTE,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  optionsStatusQuotes,
} from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { getListUser } from '~/thunks/user/userThunk';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamUser } from '~/utils/interface/user';
import { QUOTE_PERMISSIONS } from '~/utils/constants/permissions';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IPaginationResponse } from '~/utils/interface/base';
import { DateFormatEnum, RoleEnum } from '~/utils/enum/common';
import { IQueryParamQuote, IQuotes } from '~/utils/interface/quotes';
import { convertQuoteToOrder, deleteQuote, getListQuote } from '~/thunks/quote/quoteThunk';
import { formatCurrencyUSD, formatDate, getFullName } from '~/utils/helper/common';
import { quoteActions, selectIsRefreshListQuote } from '~/thunks/quote/quoteSlice';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './Quotes.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsQuotes = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string) => void,
  onConvert: (id: string) => void,
  onEdit: (id: string) => void,
  onArchive: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IQuotes>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'id',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_quote_id')}</Box>
          <SortComponent name='id' sortType={getSortType('id')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuotes) => record?.id,
      minWidth: 120,
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_client')}</Box>
          <SortComponent name='client' sortType={getSortType('client')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuotes) => {
        return (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.client} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.client?.firstName,
                  lastName: record?.client?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.client?.clientContacts?.[0]?.email}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      id: 'startDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_start_date')}</Box>
          <SortComponent name='start date' sortType={getSortType('start date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuotes) => {
        return <Typography>{formatDate(record?.startDate, DateFormatEnum.M_D_YYYY)}</Typography>;
      },
      minWidth: 140,
    },
    {
      id: 'endDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_end_date')}</Box>
          <SortComponent name='end date' sortType={getSortType('end date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuotes) => {
        return <Typography>{formatDate(record.endDate, DateFormatEnum.M_D_YYYY)}</Typography>;
      },
      minWidth: 140,
    },
    {
      id: 'createdBy',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_created_by')}</Box>
          <SortComponent name='created by' sortType={getSortType('created by')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuotes) => {
        return (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.createdBy} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.createdBy?.firstName,
                  lastName: record?.createdBy?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.createdBy?.email}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      id: 'quoteAmount',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_quote_amount')}</Box>
          <SortComponent name='quote amount' sortType={getSortType('quote amount')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuotes) => {
        return (
          <Typography className={cx('textPrice')}>
            {formatCurrencyUSD({ value: record?.total, showDecimal: true })}
          </Typography>
        );
      },
      minWidth: 160,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IQuotes) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IQuotes) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id),
                },
              ]
            : []),

          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),

          // ...(isDetail
          //   ? [
          //       {
          //         label: t('table_header_title_convert'),
          //         icon: (
          //           <img
          //             src={icons.menuIconOrderInactive}
          //             alt={t('common_alt_img', { ns: 'common/base' })}
          //             width={16}
          //             height={16}
          //           />
          //         ),
          //         onClick: () => onConvert(record.id),
          //       },
          //     ]
          //   : []),

          ...(isDelete
            ? [
                {
                  label: t('table_action_archive', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onArchive(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Quotes = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  const { canViewQuotes, canDetailQuotes, canEditQuotes, canDeleteQuotes } = usePermissions(QUOTE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const isRefreshQuoteList = useSelector(selectIsRefreshListQuote);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_QUOTE);
  const [filterParams, setFilterParams] = useState<IQueryParamQuote>(FILTER_DEFAULT_VALUES);

  const [paramObject, setParamObject] = useState<IQueryParamQuote>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [quoteList, setQuoteList] = useState<IQuotes[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [idQuote, setIdQuote] = useState<string>(EMPTY_STRING);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListQuote(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshQuoteList) return;

    handleGetListQuote(paramObject);

    dispatch(quoteActions.setRefreshQuoteList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshQuoteList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListQuote = (params: IQueryParamQuote) => {
    setIsLoading(true);

    dispatch(getListQuote(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setQuoteList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateQuote(id));
        break;
    }
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailQuote(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailQuote(id));
        break;
    }
  };

  const onConvert = (quoteId: string) => {
    setIsLoading(true);

    dispatch(convertQuoteToOrder(quoteId))
      .unwrap()
      .then((res) => {
        dispatch(quoteActions.setRefreshQuoteList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleArchive = (id: string) => {
    if (!id) return;

    setIdQuote(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdQuote(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteQuote(idQuote))
      .unwrap()
      .then(() => {
        setIdQuote(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(quoteActions.setRefreshQuoteList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsStatusQuotes,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleApplyFilter = (values: IQueryParamQuote | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        status: values?.status,
        salesAccountId: values?.salesAccountId,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      status: undefined,
      salesAccountId: undefined,
    });
  };

  const handleFilterStatusChange = (
    status: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamQuote>) => void
  ) => {
    if (!status) return;

    const { value } = status;

    onChange?.({ name: 'status', value: value });
    setFilterParams({ ...filterParams, status: value });
  };

  const handleFilterSalesPersonChange = (
    salesAccountId: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamQuote>) => void
  ) => {
    if (!salesAccountId) return;

    const { value } = salesAccountId;

    onChange?.({ name: 'salesAccountId', value: value });
    setFilterParams({ ...filterParams, salesAccountId: value });
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const fetchOptionSalePerson = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;

      const payloadParams: IQueryParamUser = {
        limit: DEFAULT_LIMIT_PAGE,
        page: page,
        role: RoleEnum.SALES_PERSON,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListUser(payloadParams)).unwrap();

        const { responses, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = responses.map((item) => ({
          value: item?.id,
          label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
        }));

        const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

        return {
          optionsResp: formatOptions,
          hasMoreResp: hasMoreResp,
        };
      } catch (_error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  //#endregion Handle Function

  return (
    <Box id='quotesPage' className={cx('container')}>
      <BaseToolbar
        title={t('quotes_title_quotes')}
        total={t('quotes_title_total_quotes', { total: canViewQuotes ? totalItems : 0 })}
      >
        <BaseFilters<IQueryParamQuote>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseSelect
                gap={10}
                height={40}
                styleType='contained'
                label={t('quotes_title_filter_status')}
                placeholder={t('quotes_title_filter_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(option) => handleFilterStatusChange(option as any, onChange)}
              />

              <BaseSelect
                isSearch
                gap={10}
                height={40}
                styleType='contained'
                label={t('quotes_title_filter_sale_person')}
                callApiFn={fetchOptionSalePerson}
                type='loadMore'
                value={value?.salesAccountId}
                onChange={(option) => handleFilterSalesPersonChange(option as any, onChange)}
              />
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewQuotes ? quoteList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsQuotes(
            t,
            sortBy,
            handleSort,
            handleView,
            onConvert,
            handleEdit,
            handleArchive,
            canDetailQuotes,
            canEditQuotes,
            canDeleteQuotes
          )}
          onPageChange={handlePageChange}
          {...(canDetailQuotes ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_archive', { ns: 'common/base' })}
          textConfirm={t('common_action_archive', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default Quotes;
